import React from 'react';
import CursorOrbCard from './CursorOrbCard';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  Avatar,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Api,
  Storage,
  Message,
  Business,
  ConnectWithoutContact,
  Verified,
  Language,
  Description,
  Bolt,
  Link as LinkIcon,
  TableChart,
  Domain,
  ViewInAr,
  Dns,
  Satellite,
  Pets,
  Mail,
  RocketLaunch,
  Cloud,
  Badge,
  Work,
  BarChart,
  OpenInNew,
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { CountUp } from './CountUp';

// Connector names for the scrolling ticker — sampled from 2,000+ library
const tickerRow1 = [
  'PostgreSQL', 'MongoDB', 'Stripe', 'Salesforce', 'AWS Lambda',
  'Apache Kafka', 'Slack', 'Shopify', 'HubSpot', 'Twilio',
  'GitHub', 'Redis', 'Snowflake', 'JazzCash', 'Datadog',
  'Zoom', 'BigQuery', 'SAP', 'SendGrid', 'Grafana',
  'Firebase', 'Neo4j', 'Terraform', 'Docker', 'Kubernetes',
];
const tickerRow2 = [
  'MySQL', 'PayPal', 'RabbitMQ', 'Microsoft Teams', 'WooCommerce',
  'Zendesk', 'WhatsApp Business', 'Splunk', 'DynamoDB', 'Easypaisa',
  'Emirates NBD', 'bKash', 'Klarna', 'Square', 'SEPA',
  'ActiveMQ', 'QuickBooks', 'New Relic', 'Daraz', 'Noon',
  'Meezan Bank', 'CircleCI', 'Oracle', 'Azure Service Bus', 'Pub/Sub',
];

function Technologies() {
  const { isLight } = useThemeMode();
  const { ref: techRef, isVisible, revealStyle, stagger } = useScrollReveal();

  const technologyCategories = [
    {
      title: 'API Protocols',
      icon: <Api sx={{ fontSize: '1.8rem' }} />,
      color: isLight ? '#6366f1' : '#0ea5e9',
      bgColor: isLight
        ? 'rgba(30, 58, 95, 0.06)'
        : 'rgba(139, 92, 246, 0.12)',
      technologies: [
        { name: 'HTTP REST', icon: <Language sx={{ fontSize: '1rem' }} />, certified: false },
        { name: 'SOAP', icon: <Description sx={{ fontSize: '1rem' }} />, certified: false },
        { name: 'gRPC', icon: <Bolt sx={{ fontSize: '1rem' }} />, certified: false },
        { name: 'GraphQL', icon: <LinkIcon sx={{ fontSize: '1rem' }} />, certified: false },
      ],
      moreCount: 20,
    },
    {
      title: 'Database Systems',
      icon: <Storage sx={{ fontSize: '1.8rem' }} />,
      color: isLight ? '#6366f1' : '#059669',
      bgColor: isLight
        ? 'rgba(99, 102, 241, 0.06)'
        : 'rgba(16, 185, 129, 0.12)',
      technologies: [
        { name: 'MySQL', icon: <TableChart sx={{ fontSize: '1rem' }} />, certified: false },
        { name: 'SQL Server', icon: <Domain sx={{ fontSize: '1rem' }} />, certified: false },
        { name: 'Oracle', icon: <ViewInAr sx={{ fontSize: '1rem' }} />, certified: false },
        { name: 'PostgreSQL', icon: <Dns sx={{ fontSize: '1rem' }} />, certified: false },
      ],
      moreCount: 80,
    },
    {
      title: 'Messaging & Queues',
      icon: <Message sx={{ fontSize: '1.8rem' }} />,
      color: isLight ? '#6366f1' : '#a855f7',
      bgColor: isLight
        ? 'rgba(99, 102, 241, 0.06)'
        : 'rgba(139, 92, 246, 0.12)',
      technologies: [
        { name: 'Apache Kafka', icon: <Satellite sx={{ fontSize: '1rem' }} />, certified: false },
        { name: 'RabbitMQ', icon: <Pets sx={{ fontSize: '1rem' }} />, certified: false },
        { name: 'ActiveMQ', icon: <Mail sx={{ fontSize: '1rem' }} />, certified: false },
        { name: 'Redis Streams', icon: <RocketLaunch sx={{ fontSize: '1rem' }} />, certified: false },
      ],
      moreCount: 45,
    },
    {
      title: 'Enterprise Solutions',
      icon: <Business sx={{ fontSize: '1.8rem' }} />,
      color: isLight ? '#6366f1' : '#ef4444',
      bgColor: isLight
        ? 'rgba(45, 95, 138, 0.06)'
        : 'rgba(239, 68, 68, 0.12)',
      technologies: [
        { name: 'Salesforce', icon: <Cloud sx={{ fontSize: '1rem' }} />, certified: false },
        { name: 'UAE Pass', icon: <Badge sx={{ fontSize: '1rem' }} />, certified: false },
        { name: 'MS Dynamics', icon: <Work sx={{ fontSize: '1rem' }} />, certified: false },
        { name: 'SAP Systems', icon: <BarChart sx={{ fontSize: '1rem' }} />, certified: false },
      ],
      moreCount: 70,
    },
  ];

  return (
    <Box
      component="section"
      id="technologies"
      sx={{
        py: { xs: 6, md: 10 },
        position: 'relative',
        overflowX: 'hidden',
        background: isLight
          ? 'transparent'
          : 'linear-gradient(135deg, #020617 0%, #0f172a 30%, #1e293b 60%, #334155 70%, #0f172a 90%, #020617 100%)',
        backdropFilter: isLight ? 'blur(20px)' : undefined,
        WebkitBackdropFilter: isLight ? 'blur(20px)' : undefined,
      }}
    >
      <Container ref={techRef} maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 }, ...revealStyle(0) }}>
          <Chip
            icon={<ConnectWithoutContact sx={{ fontSize: '1rem' }} />}
            label="CONNECTIVITY & INTEGRATIONS"
            sx={{
              mb: 2,
              px: 2,
              background: isLight
                ? 'rgba(255, 255, 255, 0.6)'
                : 'rgba(139, 92, 246, 0.15)',
              border: isLight
                ? '1px solid rgba(255, 255, 255, 0.8)'
                : '1px solid rgba(139, 92, 246, 0.4)',
              borderRadius: '50px',
              color: isLight ? '#1e293b' : '#ffffff',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '1px',
              '& .MuiChip-icon': {
                color: isLight ? '#6366f1' : '#0ea5e9',
              },
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-75%',
                width: '50%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
                transform: 'skewX(-20deg)',
                animation: 'shine-sweep 4s ease-in-out infinite',
                animationDelay: '1.5s',
              },
            }}
          />

          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3.2rem' },
              fontWeight: 900,
              lineHeight: 1.1,
              mb: 1.5,
              color: isLight ? '#0f172a' : '#ffffff',
            }}
          >
            Connect{' '}
            <Box
              component="span"
              sx={{
                background: isLight
                  ? 'linear-gradient(135deg, #6366f1, #4f46e5)'
                  : 'linear-gradient(135deg, #38bdf8, #8b5cf6)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Everything
            </Box>
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '0.95rem', md: '1.1rem' },
              color: isLight
                ? 'rgba(15, 23, 42, 0.65)'
                : 'rgba(248, 250, 252, 0.65)',
              maxWidth: 550,
              mx: 'auto',
            }}
          >
            2,000+ pre-built connectors and API integrations across databases, cloud
            platforms, enterprise apps, and regional services — ready to deploy.
          </Typography>
        </Box>

        {/* 4 Category Cards */}
        <Grid container spacing={3}>
          {technologyCategories.map((category, catIdx) => (
            <Grid item xs={12} sm={6} md={3} key={catIdx} sx={{ ...stagger(catIdx, 200) }}>
              <CursorOrbCard color={isLight ? '#6366f1' : category.color} isLight={isLight}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 3 },
                  height: '100%',
                  background: isLight ? 'rgba(255, 255, 255, 0.55)' : category.bgColor,
                  backdropFilter: 'blur(24px)',
                  WebkitBackdropFilter: 'blur(24px)',
                  border: isLight ? '1px solid rgba(255, 255, 255, 0.9)' : `1px solid ${category.color}20`,
                  borderRadius: 3,
                  boxShadow: isLight
                    ? '0 4px 24px rgba(99, 102, 241, 0.06), 0 1px 3px rgba(0,0,0,0.04)'
                    : 'none',
                  transition: 'all 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  '&:hover': {
                    background: isLight ? 'rgba(255, 255, 255, 0.7)' : category.bgColor,
                    borderColor: isLight ? 'rgba(99, 102, 241, 0.2)' : `${category.color}40`,
                    boxShadow: isLight
                      ? '0 12px 40px rgba(99, 102, 241, 0.12), 0 2px 8px rgba(0,0,0,0.04)'
                      : `0 12px 32px ${category.color}18`,
                    transform: 'translateY(-4px)',
                    '& .tech-cat-icon': {
                      animation: 'icon-pop 0.4s ease forwards',
                    },
                  },
                }}
              >
                {/* Category Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 2.5,
                    pb: 2,
                    borderBottom: isLight
                      ? '1px solid rgba(99, 102, 241, 0.08)'
                      : `1px solid ${category.color}15`,
                  }}
                >
                  <Avatar
                    className="tech-cat-icon"
                    sx={{
                      width: 40,
                      height: 40,
                      background: isLight ? 'rgba(99, 102, 241, 0.08)' : `${category.color}15`,
                      color: isLight ? '#6366f1' : category.color,
                      border: isLight ? '1px solid rgba(99, 102, 241, 0.15)' : `1.5px solid ${category.color}30`,
                    }}
                  >
                    {category.icon}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: isLight ? '#0f172a' : '#ffffff',
                        fontSize: '0.95rem',
                        lineHeight: 1.2,
                      }}
                    >
                      {category.title}
                    </Typography>
                  </Box>
                </Box>

                {/* Tech Items — vertical list */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {category.technologies.map((tech, techIdx) => (
                    <Box
                      key={techIdx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 0.75,
                        px: 1.5,
                        borderRadius: 2,
                        background: isLight
                          ? 'rgba(255,255,255,0.5)'
                          : 'rgba(15,23,42,0.4)',
                        border: isLight
                          ? '1px solid rgba(99, 102, 241, 0.06)'
                          : `1px solid ${category.color}10`,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: isLight
                            ? 'rgba(99, 102, 241, 0.04)'
                            : `${category.color}10`,
                          borderColor: isLight
                            ? 'rgba(99, 102, 241, 0.15)'
                            : `${category.color}25`,
                          transform: 'translateX(4px)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          color: isLight ? '#6366f1' : category.color,
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '1.1rem',
                        }}
                      >
                        {tech.icon}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: isLight ? '#1e293b' : '#e2e8f0',
                          fontSize: '0.8rem',
                          flex: 1,
                        }}
                      >
                        {tech.name}
                      </Typography>
                      {tech.certified && (
                        <Tooltip title="Certified Integration" arrow>
                          <Verified
                            sx={{
                              fontSize: '0.9rem',
                              color: isLight ? '#6366f1' : category.color,
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  ))}
                </Box>

                {/* More indicator */}
                <Box
                  sx={{
                    mt: 2,
                    pt: 1.5,
                    borderTop: isLight
                      ? '1px dashed rgba(99, 102, 241, 0.12)'
                      : `1px dashed ${category.color}20`,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: isLight ? '#6366f1' : category.color,
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                    }}
                  >
                    +{category.moreCount} more
                  </Typography>
                </Box>
              </Paper>
              </CursorOrbCard>
            </Grid>
          ))}
        </Grid>

        {/* Scrolling connector ticker */}
        <Box
          sx={{
            mt: 5,
            mb: 3,
            overflow: 'hidden',
            py: 2,
            maskImage:
              'linear-gradient(to right, transparent, black 60px, black calc(100% - 60px), transparent)',
            WebkitMaskImage:
              'linear-gradient(to right, transparent, black 60px, black calc(100% - 60px), transparent)',
          }}
        >
          {/* Row 1 — scrolling left */}
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              mb: 1.5,
              width: 'fit-content',
              '@keyframes connectorScrollLeft': {
                from: { transform: 'translateX(0)' },
                to: { transform: 'translateX(-50%)' },
              },
              animation: 'connectorScrollLeft 60s linear infinite',
              '&:hover': { animationPlayState: 'paused' },
            }}
          >
            {[...tickerRow1, ...tickerRow1].map((name, i) => (
              <Chip
                key={i}
                label={name}
                size="small"
                sx={{
                  flexShrink: 0,
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  background: isLight
                    ? 'rgba(99, 102, 241, 0.06)'
                    : 'rgba(139, 92, 246, 0.1)',
                  border: isLight
                    ? '1px solid rgba(99, 102, 241, 0.12)'
                    : '1px solid rgba(139, 92, 246, 0.2)',
                  color: isLight ? '#4f46e5' : '#c4b5fd',
                  borderRadius: '6px',
                  '& .MuiChip-label': { px: 1.5 },
                  transition: 'all 0.2s ease',
                  cursor: 'default',
                  '&:hover': {
                    transform: 'scale(1.08)',
                    boxShadow: isLight
                      ? '0 4px 12px rgba(99, 102, 241, 0.15)'
                      : '0 4px 12px rgba(139, 92, 246, 0.25)',
                    background: isLight
                      ? 'rgba(99, 102, 241, 0.12)'
                      : 'rgba(139, 92, 246, 0.2)',
                  },
                }}
              />
            ))}
          </Box>

          {/* Row 2 — scrolling right */}
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              width: 'fit-content',
              '@keyframes connectorScrollRight': {
                from: { transform: 'translateX(-50%)' },
                to: { transform: 'translateX(0)' },
              },
              animation: 'connectorScrollRight 55s linear infinite',
              '&:hover': { animationPlayState: 'paused' },
            }}
          >
            {[...tickerRow2, ...tickerRow2].map((name, i) => (
              <Chip
                key={i}
                label={name}
                size="small"
                sx={{
                  flexShrink: 0,
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  background: isLight
                    ? 'rgba(5, 150, 105, 0.06)'
                    : 'rgba(16, 185, 129, 0.1)',
                  border: isLight
                    ? '1px solid rgba(5, 150, 105, 0.12)'
                    : '1px solid rgba(16, 185, 129, 0.2)',
                  color: isLight ? '#059669' : '#6ee7b7',
                  borderRadius: '6px',
                  '& .MuiChip-label': { px: 1.5 },
                  transition: 'all 0.2s ease',
                  cursor: 'default',
                  '&:hover': {
                    transform: 'scale(1.08)',
                    boxShadow: isLight
                      ? '0 4px 12px rgba(5, 150, 105, 0.15)'
                      : '0 4px 12px rgba(16, 185, 129, 0.25)',
                    background: isLight
                      ? 'rgba(5, 150, 105, 0.12)'
                      : 'rgba(16, 185, 129, 0.2)',
                  },
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Bottom stats + CTA */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              color: isLight
                ? 'rgba(15, 23, 42, 0.55)'
                : 'rgba(248, 250, 252, 0.55)',
              fontSize: '0.85rem',
              mb: 3,
            }}
          >
            <Box
              component="span"
              sx={{
                color: isLight ? '#6366f1' : '#a855f7',
                fontWeight: 700,
                fontSize: '0.95rem',
              }}
            >
              <CountUp end={2000} trigger={isVisible} suffix="+" separator duration={1800} />
            </Box>{' '}
            pre-built connectors &bull;{' '}
            <Box
              component="span"
              sx={{
                color: isLight ? '#6366f1' : '#a855f7',
                fontWeight: 700,
                fontSize: '0.95rem',
              }}
            >
              <CountUp end={35} trigger={isVisible} suffix="+" duration={1500} />
            </Box>{' '}
            categories &bull;{' '}
            <Box
              component="span"
              sx={{
                color: isLight ? '#6366f1' : '#a855f7',
                fontWeight: 700,
                fontSize: '0.95rem',
              }}
            >
              <CountUp end={6} trigger={isVisible} duration={1200} />
            </Box>{' '}
            regional markets &bull; Custom integrations in 24–48h
          </Typography>

          <Button
            variant="outlined"
            endIcon={<OpenInNew sx={{ fontSize: '0.9rem !important' }} />}
            href="https://studio.alphanetix.io"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              borderColor: isLight
                ? 'rgba(99, 102, 241, 0.3)'
                : 'rgba(139, 92, 246, 0.3)',
              color: isLight ? '#4f46e5' : '#c4b5fd',
              fontWeight: 600,
              fontSize: '0.85rem',
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              py: 1,
              transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              '&:hover': {
                borderColor: isLight ? '#6366f1' : '#a855f7',
                background: isLight
                  ? 'rgba(99, 102, 241, 0.04)'
                  : 'rgba(139, 92, 246, 0.08)',
                transform: 'translateY(-2px)',
              },
              '&:active': {
                transform: 'translateY(0) scale(0.96)',
                transition: 'transform 0.1s ease',
              },
            }}
          >
            Explore Connector Library
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default Technologies;
